import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Flame, Eye, EyeOff, ArrowRight, Sparkles, Check, X, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Password rule indicator component
const PasswordRule = ({ met, text }) => (
  <div className={`flex items-center gap-2 text-xs ${met ? 'text-green-500' : 'text-muted-foreground'}`}>
    {met ? <Check className="w-3 h-3" /> : <X className="w-3 h-3" />}
    <span>{text}</span>
  </div>
);

const AuthPage = () => {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const refCode = searchParams.get('ref') || '';
  const [mode, setMode] = useState(refCode ? 'signup' : 'login'); // 'login', 'signup', 'forgot', 'reset'
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [resetToken, setResetToken] = useState('');
  const [touched, setTouched] = useState({
    name: false,
    email: false,
    password: false,
    confirmPassword: false
  });
  const { login, register } = useAuth();
  const navigate = useNavigate();

  // Check for reset token in URL
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const token = params.get('reset_token');
    if (token) {
      setResetToken(token);
      setMode('reset');
    }
  }, [location]);

  // Password validation rules
  const passwordRules = {
    minLength: password.length >= 8,
    hasUppercase: /[A-Z]/.test(password),
    hasLowercase: /[a-z]/.test(password),
    hasNumber: /\d/.test(password),
    matches: password === confirmPassword && confirmPassword.length > 0
  };

  const isPasswordValid = passwordRules.minLength && 
    passwordRules.hasUppercase && 
    passwordRules.hasLowercase && 
    passwordRules.hasNumber;

  const canSubmitSignup = name.trim() && email && isPasswordValid && passwordRules.matches;
  const canSubmitLogin = email && password;
  const canSubmitForgot = email;
  const canSubmitReset = isPasswordValid && passwordRules.matches;

  const handleBlur = (field) => {
    setTouched(prev => ({ ...prev, [field]: true }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (mode === 'login') {
        await login(email, password);
        toast.success('Welcome back to Delphi AI!');
        navigate('/dashboard');
      } else if (mode === 'signup') {
        // Client-side validation
        if (!name.trim()) {
          toast.error('Name is required');
          setLoading(false);
          return;
        }
        if (!isPasswordValid) {
          toast.error('Password does not meet requirements');
          setLoading(false);
          return;
        }
        if (password !== confirmPassword) {
          toast.error('Passwords do not match');
          setLoading(false);
          return;
        }

        await register(name.trim(), email, password, confirmPassword, refCode);
        toast.success('Welcome to Delphi AI! Your journey begins.');
        navigate('/dashboard');
      } else if (mode === 'forgot') {
        const response = await axios.post(`${API}/auth/forgot-password`, { email });
        toast.success('If an account exists, a reset link has been sent');
        // For testing - show token (remove in production)
        if (response.data.reset_token) {
          setResetToken(response.data.reset_token);
          setMode('reset');
        }
      } else if (mode === 'reset') {
        if (!isPasswordValid) {
          toast.error('Password does not meet requirements');
          setLoading(false);
          return;
        }
        if (password !== confirmPassword) {
          toast.error('Passwords do not match');
          setLoading(false);
          return;
        }

        await axios.post(`${API}/auth/reset-password`, {
          token: resetToken,
          password,
          confirm_password: confirmPassword
        });
        toast.success('Password reset successful! Please sign in.');
        setMode('login');
        setPassword('');
        setConfirmPassword('');
      }
    } catch (error) {
      const detail = error.response?.data?.detail;
      const status = error.response?.status;
      let msg;
      if (typeof detail === 'object' && detail?.message) {
        msg = detail.message;
      } else if (typeof detail === 'string') {
        msg = detail;
      } else if (status === 0 || !error.response) {
        msg = 'Cannot reach server. Check your connection and try again.';
      } else {
        msg = `Request failed (${status || 'unknown'}). Please try again.`;
      }
      toast.error(msg);
      console.error('[Auth]', status, detail);
    } finally {
      setLoading(false);
    }
  };

  const switchMode = (newMode) => {
    setMode(newMode);
    setPassword('');
    setConfirmPassword('');
    setTouched({ name: false, email: false, password: false, confirmPassword: false });
  };

  return (
    <div className="min-h-screen bg-background flex">
      {/* Left Panel - Branding */}
      <div className="hidden lg:flex lg:w-1/2 bg-card relative overflow-hidden">
        <div className="absolute inset-0 bg-grid opacity-30" />
        <div className="absolute inset-0 hero-bg" />
        <div className="relative z-10 flex flex-col justify-center items-center p-12 w-full">
          <Link to="/" className="flex items-center gap-3 mb-12">
            <div className="w-16 h-16 rounded-2xl oracle-flame-bg flex items-center justify-center animate-pulse-glow">
              <Flame className="w-10 h-10 text-white" />
            </div>
            <span className="text-4xl font-bold oracle-flame">Delphi AI</span>
          </Link>
          
          <div className="max-w-md text-center">
            <h2 className="text-3xl font-bold mb-4">
              Your AI-Powered <span className="oracle-flame">Intelligence System</span>
            </h2>
            <p className="text-muted-foreground text-lg">
              Create viral content, build AI influencers, and scale your brand with the power of the Oracle.
            </p>
          </div>

          <div className="mt-12 grid grid-cols-2 gap-6 w-full max-w-md">
            {[
              { label: 'AI Influencers', desc: 'Create your digital persona' },
              { label: 'Instant Reels', desc: 'One-click content' },
              { label: 'Ask Delphi', desc: 'Your AI oracle' },
              { label: '150+ Languages', desc: 'Global reach' }
            ].map((item, i) => (
              <div key={i} className="card-oracle p-4 text-center">
                <Sparkles className="w-5 h-5 text-primary mx-auto mb-2" />
                <p className="font-medium text-sm">{item.label}</p>
                <p className="text-xs text-muted-foreground">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Right Panel - Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          {/* Mobile Logo */}
          <Link to="/" className="lg:hidden flex items-center gap-2 justify-center mb-8">
            <div className="w-10 h-10 rounded-xl oracle-flame-bg flex items-center justify-center">
              <Flame className="w-6 h-6 text-white" />
            </div>
            <span className="text-2xl font-bold oracle-flame">Delphi AI</span>
          </Link>

          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold mb-2">
              {mode === 'login' && 'Welcome Back'}
              {mode === 'signup' && 'Start Your Journey'}
              {mode === 'forgot' && 'Reset Password'}
              {mode === 'reset' && 'Create New Password'}
            </h1>
            <p className="text-muted-foreground">
              {mode === 'login' && 'Sign in to continue creating'}
              {mode === 'signup' && 'Create your account to begin'}
              {mode === 'forgot' && 'Enter your email to receive a reset link'}
              {mode === 'reset' && 'Choose a strong password for your account'}
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Name Field - Signup only */}
            {mode === 'signup' && (
              <div className="space-y-2">
                <Label htmlFor="name">Full Name <span className="text-destructive">*</span></Label>
                <Input
                  id="name"
                  data-testid="auth-name-input"
                  type="text"
                  placeholder="Enter your full name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  onBlur={() => handleBlur('name')}
                  className={`input-oracle ${touched.name && !name.trim() ? 'border-destructive' : ''}`}
                />
                {touched.name && !name.trim() && (
                  <p className="text-xs text-destructive">Name is required</p>
                )}
              </div>
            )}

            {/* Email Field - Not for reset mode */}
            {mode !== 'reset' && (
              <div className="space-y-2">
                <Label htmlFor="email">Email <span className="text-destructive">*</span></Label>
                <Input
                  id="email"
                  data-testid="auth-email-input"
                  type="email"
                  placeholder="Enter your email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  onBlur={() => handleBlur('email')}
                  className="input-oracle"
                  required
                />
              </div>
            )}

            {/* Password Field - Login, Signup, Reset */}
            {(mode === 'login' || mode === 'signup' || mode === 'reset') && (
              <div className="space-y-2">
                <Label htmlFor="password">
                  {mode === 'reset' ? 'New Password' : mode === 'signup' ? 'Create Password' : 'Password'}
                  <span className="text-destructive"> *</span>
                </Label>
                <div className="relative">
                  <Input
                    id="password"
                    data-testid="auth-password-input"
                    type={showPassword ? 'text' : 'password'}
                    placeholder={mode === 'reset' ? 'Enter new password' : 'Enter your password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    onBlur={() => handleBlur('password')}
                    className="input-oracle pr-12"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                    data-testid="toggle-password-visibility"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>

                {/* Password Rules - Signup and Reset only */}
                {(mode === 'signup' || mode === 'reset') && touched.password && (
                  <div className="mt-2 space-y-1 p-3 bg-secondary/30 rounded-lg">
                    <PasswordRule met={passwordRules.minLength} text="At least 8 characters" />
                    <PasswordRule met={passwordRules.hasUppercase} text="At least 1 uppercase letter" />
                    <PasswordRule met={passwordRules.hasLowercase} text="At least 1 lowercase letter" />
                    <PasswordRule met={passwordRules.hasNumber} text="At least 1 number" />
                  </div>
                )}
              </div>
            )}

            {/* Confirm Password Field - Signup and Reset */}
            {(mode === 'signup' || mode === 'reset') && (
              <div className="space-y-2">
                <Label htmlFor="confirmPassword">Confirm Password <span className="text-destructive">*</span></Label>
                <div className="relative">
                  <Input
                    id="confirmPassword"
                    data-testid="auth-confirm-password-input"
                    type={showConfirmPassword ? 'text' : 'password'}
                    placeholder="Confirm your password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    onBlur={() => handleBlur('confirmPassword')}
                    className={`input-oracle pr-12 ${touched.confirmPassword && !passwordRules.matches ? 'border-destructive' : ''}`}
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                    data-testid="toggle-confirm-password-visibility"
                  >
                    {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
                {touched.confirmPassword && confirmPassword && !passwordRules.matches && (
                  <p className="text-xs text-destructive">Passwords do not match</p>
                )}
                {touched.confirmPassword && passwordRules.matches && (
                  <p className="text-xs text-green-500 flex items-center gap-1">
                    <Check className="w-3 h-3" /> Passwords match
                  </p>
                )}
              </div>
            )}

            {/* Forgot Password Link - Login only */}
            {mode === 'login' && (
              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={() => switchMode('forgot')}
                  className="text-sm text-primary hover:underline"
                  data-testid="forgot-password-link"
                >
                  Forgot password?
                </button>
              </div>
            )}

            {/* Submit Button */}
            <Button
              type="submit"
              data-testid="auth-submit-btn"
              className="w-full btn-oracle h-12 text-base"
              disabled={loading || 
                (mode === 'signup' && !canSubmitSignup) ||
                (mode === 'login' && !canSubmitLogin) ||
                (mode === 'forgot' && !canSubmitForgot) ||
                (mode === 'reset' && !canSubmitReset)
              }
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <Loader2 className="w-5 h-5 animate-spin" />
                  {mode === 'login' && 'Signing in...'}
                  {mode === 'signup' && 'Creating account...'}
                  {mode === 'forgot' && 'Sending reset link...'}
                  {mode === 'reset' && 'Resetting password...'}
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  {mode === 'login' && 'Sign In'}
                  {mode === 'signup' && 'Create Account'}
                  {mode === 'forgot' && 'Send Reset Link'}
                  {mode === 'reset' && 'Reset Password'}
                  <ArrowRight className="w-5 h-5" />
                </span>
              )}
            </Button>
          </form>

          {/* Mode Switching */}
          <div className="mt-6 text-center">
            {mode === 'login' && (
              <p className="text-muted-foreground">
                Don&apos;t have an account?
                <button
                  data-testid="auth-toggle-btn"
                  onClick={() => switchMode('signup')}
                  className="ml-2 text-primary hover:underline font-medium"
                >
                  Sign Up
                </button>
              </p>
            )}
            {mode === 'signup' && (
              <p className="text-muted-foreground">
                Already have an account?
                <button
                  data-testid="auth-toggle-btn"
                  onClick={() => switchMode('login')}
                  className="ml-2 text-primary hover:underline font-medium"
                >
                  Sign In
                </button>
              </p>
            )}
            {(mode === 'forgot' || mode === 'reset') && (
              <p className="text-muted-foreground">
                Remember your password?
                <button
                  onClick={() => switchMode('login')}
                  className="ml-2 text-primary hover:underline font-medium"
                >
                  Sign In
                </button>
              </p>
            )}
          </div>

          <p className="mt-8 text-center text-xs text-muted-foreground">
            By continuing, you agree to our{' '}
            <Link to="/terms" className="text-primary hover:underline">Terms of Service</Link>
            {' '}and{' '}
            <Link to="/privacy" className="text-primary hover:underline">Privacy Policy</Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default AuthPage;
